import React, { useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import HrbTranslate from '../../../components/language/HrbTranslate';
import ContentPlain from '../../../components/layout/ContentPlain';
import Section from '../../../components/layout/Section';
import ProductBadge from '../../../components/library/badges/ProductBadge';
import BackLink from '../../../components/library/common/BackLink';
import DateTime from '../../../components/library/common/DateTime';
import EmdashText from '../../../components/library/common/EmdashText';
import VerticalNameChildren from '../../../components/library/layout/VerticalNameChildren';
import Skeleton from '../../../components/library/loaders/Skeleton';
import ActivationCodes from './ActivationCodes';
import '../../../stylesheets/productPages.scss';

const DiyReturnSummary = (props) => {

    const { isLoading, returnInfo } = props

    const match = useRouteMatch();
    const { taxYear, clientId } = match.params

    const [addOns, setAddOns] = useState([])
    const [nameContent, setNameContent] = useState(null);

    const buildName = (firstName, lastName) => {
        if (!firstName) {
            return "-";
        }
        if (!lastName) {
            return firstName;
        }
        return `${firstName} ${lastName}`;
    }

    useEffect(() => {
        if (returnInfo.upgrades && returnInfo.upgrades.length > 0) {
            let addons = returnInfo.upgrades.map(upgrade => <HrbTranslate key={'upgrade-' + upgrade.key} text={`ReturnInfo.Upgrade.${upgrade}`} />)
            setAddOns(addons)
        } else {
            setAddOns([])
        }
    }, [returnInfo.upgrades])

    useEffect(() => {
        if (Boolean(returnInfo)) {
            let content = returnInfo.returnType === 'SINGLE' ?
                <HrbTranslate text='ReturnInfo.NameFormatter.SINGLE'
                    params={{ primary: buildName(returnInfo.primaryFirstName, returnInfo.primaryLastName) }} /> :
                <HrbTranslate text='ReturnInfo.NameFormatter.COUPLE'
                    params={{
                        primary: buildName(returnInfo.primaryFirstName, returnInfo.primaryLastName),
                        spouse: buildName(returnInfo.spouseFirstName, returnInfo.spouseLastName)
                    }} />

            setNameContent(content)
        } else {
            setNameContent(null)
        }
    }, [returnInfo.primaryFirstName, returnInfo.primaryLastName, returnInfo.spouseFirstName, returnInfo.spouseLastName, returnInfo.returnType])

    return (
        <Section sectionId='section-return-summary' className='white'>
            <ContentPlain elementId='content-greeting'>
                <div className='columns tickets level'>
                    <div className='column is-1'>
                        <ProductBadge productCode="DIY" />
                    </div>
                    <div className='column is-8'>
                        {isLoading && <Skeleton />}
                        {!isLoading && Boolean(returnInfo) && <h1>{nameContent}</h1>}
                        {!isLoading && !returnInfo && '-'}
                    </div>
                    <div className='column is-2'>
                        <BackLink isRight />
                    </div>
                    <div className='column is-1' />
                </div>
                <div className='columns tickets level separator-border'>
                    <div className='column is-1' />
                    <div className='column is-10'>
                        <hr />
                    </div>
                    <div className='column is-1' />
                </div>
                <div className='columns tickets'>
                    <div className='column is-1' />
                    <div className='column is-4'>
                        {isLoading && <Skeleton />}
                        {!isLoading && <h4><HrbTranslate text='Tickets.Labels.TaxYear' params={{ TaxYear: returnInfo.taxYear }} /></h4>}
                    </div>
                </div>
                <div className='columns tickets'>
                    <div className='column is-1' />
                    <div className='column is-4'>
                        <VerticalNameChildren
                            textKey="ReturnInfo.Labels.Product"
                            loading={isLoading}>
                            <p className='p1 medium-weight'><HrbTranslate text={`ReturnInfo.ProductName.${returnInfo.product || 'DIY'}.${returnInfo.platforms && returnInfo.platforms.length ? returnInfo.platforms[0] : 'ONLINE'}`} /></p>
                        </VerticalNameChildren>
                    </div>
                    <div className='column is-4'>
                        <VerticalNameChildren
                            textKey="ReturnInfo.Labels.Package"
                            loading={isLoading}>
                            {returnInfo.platforms ?
                                <p className='p1 medium-weight'><HrbTranslate text={`ReturnInfo.Package.${(returnInfo.platforms && returnInfo.platforms.length ? returnInfo.platforms[0] : 'ONLINE')}.${returnInfo.package || 'TIER1'}`} /></p> :
                                <p className='p1 medium-weight'><EmdashText /></p>
                            }
                        </VerticalNameChildren>
                    </div>
                    <div className='column is-2'>
                        <VerticalNameChildren
                            textKey="ReturnInfo.Labels.Type"
                            labelClassName='no-wrap'
                            loading={isLoading}>
                            {returnInfo.returnType ?
                                <p className='p1 medium-weight'><HrbTranslate text={`ReturnInfo.Type.${returnInfo.returnType}`} /></p> :
                                <p className='p1 medium-weight'><EmdashText /></p>
                            }
                        </VerticalNameChildren>
                    </div>
                    <div className='column is-1' />
                </div>
                <div className='columns tickets'>
                    <div className='column is-1' />
                    <div className='column is-4'>
                        <VerticalNameChildren
                            textKey="ReturnInfo.Labels.AddOns"
                            loading={isLoading}>
                            {addOns && addOns.length > 0 ?
                                <p className='medium-weight'>{addOns.reduce((result, child, index) => {
                                    if (index < addOns.length - 1) {
                                        return result.concat([child, ' | '])
                                    } else {
                                        return result.concat(child)
                                    }
                                },[])}</p> :
                                <p className='p1 medium-weight'><EmdashText /></p>
                            }
                        </VerticalNameChildren>
                    </div>
                    <div className='column is-4'>
                        <VerticalNameChildren
                            textKey="ReturnInfo.Labels.LastModified"
                            loading={isLoading}>
                            {returnInfo.lastModified ?
                                <p className='p1 medium-weight'><DateTime value={returnInfo.lastModified} dateAndTime /></p> :
                                <p className='p1 medium-weight'><EmdashText /></p>
                            }
                        </VerticalNameChildren>
                    </div>
                    <div className='column is-2'>
                        <VerticalNameChildren
                            textKey="ReturnInfo.Labels.ProvinceTerritory"
                            loading={isLoading}>
                            {returnInfo.province ?
                                <p className='p1 medium-weight'><HrbTranslate text={`Account.Provinces.${returnInfo.province}`} /></p> :
                                <p className='p1 medium-weight'><EmdashText /></p>
                            }
                        </VerticalNameChildren>
                    </div>
                    <div className='column is-1' />
                </div>
                <ActivationCodes productId={returnInfo.productId} taxYear={taxYear} clientId={clientId} />
            </ContentPlain>
        </Section>
    );
}

DiyReturnSummary.whyDidYouRender = false;

export default DiyReturnSummary